import { createEnv } from "@t3-oss/env-core";
import { z } from "zod";

/**
 * Validated at module load, so a missing or malformed variable fails at boot
 * with the offending key named instead of surfacing as a confusing runtime bug.
 */
export const env = createEnv({
  clientPrefix: "VITE_",
  client: {
    VITE_APP_ENV: z.enum(["local", "preview", "production"]).default("local"),
    VITE_DATA_SOURCE: z.enum(["mock", "supabase"]).default("mock"),
    VITE_MOCK_ROLE: z.enum(["Staff", "Manager", "Admin"]).default("Admin"),
    // Email domain the Entra SAML connection is registered under in Supabase.
    VITE_SSO_DOMAIN: z.string().min(1).optional(),
    // Demo tools on the login page: password sign-in and a quick account picker.
    // Staging only, for client demos while SAML is not set up.
    VITE_DEV_TOOLS: z.enum(["true", "false"]).default("false"),
  },
  // Listed one by one: passing `import.meta.env` whole makes Vite inline every
  // VITE_* variable into this module, including VITE_DEMO_PASSWORD, which the
  // demo menu reads itself so it stays in that chunk.
  runtimeEnv: {
    VITE_APP_ENV: import.meta.env.VITE_APP_ENV,
    VITE_DATA_SOURCE: import.meta.env.VITE_DATA_SOURCE,
    VITE_MOCK_ROLE: import.meta.env.VITE_MOCK_ROLE,
    VITE_SSO_DOMAIN: import.meta.env.VITE_SSO_DOMAIN,
    VITE_DEV_TOOLS: import.meta.env.VITE_DEV_TOOLS,
  },
  emptyStringAsUndefined: true,
});

export const IS_MOCK_DATA = env.VITE_DATA_SOURCE === "mock";

/**
 * The demo account picker. On for the local Supabase stack, and on staging when
 * VITE_DEV_TOOLS is set. Never in production, whatever the flag says.
 */
export const DEV_TOOLS_ENABLED =
  env.VITE_APP_ENV !== "production" &&
  !IS_MOCK_DATA &&
  (env.VITE_APP_ENV === "local" || env.VITE_DEV_TOOLS === "true");

/**
 * Password sign-in exists for the seeded local accounts and the staging demo
 * accounts, neither of which can sign in through an identity provider yet.
 * Production is SSO-only.
 */
export const ALLOW_PASSWORD_SIGN_IN = DEV_TOOLS_ENABLED;
